// Sidebar interactivity:
//   - Top-level rail with always-flyout sub-menus
//   - Collapse / expand toggle (cookie-persisted)
//   - Drag-to-resize handle (cookie-persisted, 220–360 px)
//
// Markup contract: see app/views/shared/_sidebar.html.erb

const STATE_COOKIE = "jestion_sidebar";
const WIDTH_COOKIE = "jestion_sidebar_w";
const ONE_YEAR = 60 * 60 * 24 * 365;

const writeCookie = (name, value) => {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${ONE_YEAR}; path=/; SameSite=Lax`;
};

const positionFlyout = (group) => {
  const trigger = group.querySelector("[data-group-toggle]");
  const flyout = group.querySelector("[data-group-flyout]");
  if (!trigger || !flyout) return;

  const rect = trigger.getBoundingClientRect();
  const flyoutRect = flyout.getBoundingClientRect();
  const margin = 8;

  let left = rect.right + margin;
  let top = rect.top;

  // Profile flyout opens upwards (pinned to the trigger's bottom edge)
  if (group.dataset.groupId === "profile") {
    top = rect.bottom - flyoutRect.height;
    if (top < margin) top = rect.top;
  }

  const maxLeft = window.innerWidth - flyoutRect.width - margin;
  if (left > maxLeft) left = Math.max(margin, maxLeft);
  const maxTop = window.innerHeight - flyoutRect.height - margin;
  if (top > maxTop) top = maxTop;
  if (top < margin) top = margin;

  flyout.style.left = `${left}px`;
  flyout.style.top = `${top}px`;
};

// Reset the sidebar's "initialized" flag on Turbo back/forward cache restore
// so the next `turbo:load` re-binds listeners on the freshly-restored DOM.
// Without this, the cached snapshot keeps the data-initialized attribute,
// initSidebar bails out, and the user lands on a sidebar with dead listeners.
if (!window.__jestionSidebarResetWired) {
  document.addEventListener("turbo:before-cache", () => {
    const sb = document.getElementById("app-sidebar");
    if (sb) sb.removeAttribute("data-initialized");
    // Also collapse any flyout that was open at navigation time so the cached
    // snapshot looks clean.
    document.querySelectorAll(".sidebar__group.is-flyout-open").forEach((g) => {
      g.classList.remove("is-flyout-open");
      g.querySelector("[data-group-toggle]")?.setAttribute("aria-expanded", "false");
    });
  });
  window.__jestionSidebarResetWired = true;
}

const initSidebar = () => {
  const sidebar = document.getElementById("app-sidebar");
  if (!sidebar || sidebar.dataset.initialized === "true") return;
  sidebar.dataset.initialized = "true";

  const groups = sidebar.querySelectorAll(".sidebar__group[data-group-id]");

  const closeAllGroups = (except = null) => {
    groups.forEach((g) => {
      if (g === except) return;
      if (!g.classList.contains("is-flyout-open")) return;
      g.classList.remove("is-flyout-open");
      g.querySelector("[data-group-toggle]")?.setAttribute("aria-expanded", "false");
    });
  };

  // ---- collapse / expand --------------------------------------------------
  document.getElementById("sidebar-toggle")?.addEventListener("click", () => {
    const next = sidebar.dataset.state === "collapsed" ? "expanded" : "collapsed";
    sidebar.dataset.state = next;
    writeCookie(STATE_COOKIE, next);
    closeAllGroups();
  });

  // ---- group flyouts -----------------------------------------------------
  groups.forEach((group) => {
    const trigger = group.querySelector("[data-group-toggle]");
    if (!trigger) return;

    trigger.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      const willOpen = !group.classList.contains("is-flyout-open");
      closeAllGroups(group);
      group.classList.toggle("is-flyout-open", willOpen);
      trigger.setAttribute("aria-expanded", String(willOpen));
      if (willOpen) requestAnimationFrame(() => positionFlyout(group));
    });
  });

  const repositionOpenFlyouts = () => {
    groups.forEach((g) => g.classList.contains("is-flyout-open") && positionFlyout(g));
  };
  window.addEventListener("resize", repositionOpenFlyouts);
  window.addEventListener("scroll", repositionOpenFlyouts, { passive: true });

  // Close on outside click
  document.addEventListener("click", (event) => {
    if (sidebar.contains(event.target)) return;
    if (event.target.closest(".sidebar__flyout")) return;
    closeAllGroups();
    if (window.innerWidth <= 900) sidebar.dataset.state = "collapsed";
  });

  // Close after navigating via a link
  sidebar.querySelectorAll(".sidebar__flyout-link, .sidebar__link").forEach((link) => {
    if (link.dataset.groupToggle) return; // group triggers are handled above
    link.addEventListener("click", () => closeAllGroups());
  });

  // ---- resize handle ------------------------------------------------------
  const resizer = sidebar.querySelector(".sidebar__resizer");
  if (resizer) {
    let startX = 0;
    let startW = 0;

    const stop = () => {
      sidebar.classList.remove("is-resizing");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", stop);
      document.body.style.cursor = "";
      const finalW = parseInt(getComputedStyle(sidebar).width, 10);
      if (finalW >= 220 && finalW <= 360) writeCookie(WIDTH_COOKIE, String(finalW));
    };

    const onMove = (event) => {
      const delta = event.clientX - startX;
      const next = Math.min(360, Math.max(220, startW + delta));
      sidebar.style.setProperty("--sidebar-w-expanded", `${next}px`);
    };

    resizer.addEventListener("mousedown", (event) => {
      if (sidebar.dataset.state !== "expanded") return;
      event.preventDefault();
      startX = event.clientX;
      startW = parseInt(getComputedStyle(sidebar).width, 10);
      sidebar.classList.add("is-resizing");
      document.body.style.cursor = "col-resize";
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", stop);
    });
  }
};

export { initSidebar };
